import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { DrugTestingScheduleStatus } from 'models/drugTesting';

import { IDrugTestAppointment, REASON_VIEW_MORE_CHAR_LIMIT } from '../types';
import {
  EMPTY_VALUE,
  StatusTone,
  getAppointmentAddress,
  getAppointmentDetails,
  getAppointmentStatusDisplay,
  getAppointmentSubstance,
  getBatResult,
  getResultLabelKey,
  isBatResultVisible,
} from '../appointmentDisplay';
import { useStyles } from '../styles';
import ActionsMenu from './ActionsMenu';

// The actions menu (three-dot) is only available for scheduled or missed appointments.
const MENU_STATUSES = [DrugTestingScheduleStatus.Scheduled, DrugTestingScheduleStatus.MissedAppointment];
// Edit is available for everyone unless the appointment is missed or a no-show.
const NON_EDITABLE_STATUSES = [DrugTestingScheduleStatus.MissedAppointment, DrugTestingScheduleStatus.NoShow];

interface AppointmentCardProps {
  appointment: IDrugTestAppointment;
  onEdit: (appointment: IDrugTestAppointment) => void;
  onDelete: (appointment: IDrugTestAppointment) => void;
  onViewMore: (appointment: IDrugTestAppointment) => void;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment, onEdit, onDelete, onViewMore }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const initial = appointment.facilityName.trim().charAt(0).toUpperCase() || 'A';
  const showViewMore = (appointment.reason?.length || 0) > REASON_VIEW_MORE_CHAR_LIMIT;
  const showMenu = appointment.status !== undefined && MENU_STATUSES.includes(appointment.status);
  const canEdit = appointment.status === undefined || !NON_EDITABLE_STATUSES.includes(appointment.status);

  // Derived display values. Status color follows the portal's own convention
  // (Pending amber, Approved green, Denied red); other statuses render as plain text.
  const statusToneClass: Record<StatusTone, string> = {
    pending: classes.statusPending,
    approved: classes.statusApproved,
    denied: classes.statusDenied,
    normal: classes.detailValue,
  };
  const statusDisplay = getAppointmentStatusDisplay(appointment);
  const statusText = statusDisplay.labelKey ? t(statusDisplay.labelKey) : EMPTY_VALUE;
  const statusClass = statusToneClass[statusDisplay.tone];

  const resultKey = getResultLabelKey(appointment.drugTestingScheduleResult);
  const resultText = resultKey ? t(resultKey) : EMPTY_VALUE;

  const address = getAppointmentAddress(appointment.facilityAddress);
  const substance = getAppointmentSubstance(appointment?.resultSentToLabTestSubstances);

  const showBat = isBatResultVisible(appointment.drugTestingScheduleResult);
  const batResult = getBatResult(appointment);

  const details = getAppointmentDetails(appointment);
  const detailsText = details.labelKey ? t(details.labelKey) : details.text;

  const handleMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom - 24, right: window.innerWidth - rect.right + 18 });
    }
    setMenuOpen(true);
  };
  const handleMenuClose = () => setMenuOpen(false);

  return (
    <Box className={classes.appointmentCard} data-testid={`appointment-card-${appointment.id}`}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderContent}>
          <Box className={classes.avatar}>{initial}</Box>
          <Box className={classes.facilityInfo}>
            <p className={classes.facilityName} data-testid={`appointment-card-facility-${appointment.id}`}>
              {appointment.facilityName}
            </p>
            <p className={classes.appointmentDate}>{appointment.appointmentDate}</p>
          </Box>
        </Box>
        {showMenu && (
          <IconButton
            ref={buttonRef}
            size="small"
            className={classes.menuButton}
            data-testid={`appointment-menu-button-${appointment.id}`}
            onClick={handleMenuOpen}
            aria-label="actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box className={classes.cardDetails}>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.project')}</p>
          <p className={classes.detailValue}>{appointment.projectName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.type')}</p>
          <p className={classes.detailValue}>{appointment.typeName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.panel')}</p>
          <p className={classes.detailValue}>{appointment.panelName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.substance')}</p>
          <p className={classes.detailValue}>{substance}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.address')}</p>
          <p className={classes.detailValue}>{address}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.reason')}</p>
          <p className={classes.reasonText}>
            {appointment.reason}
            {showViewMore && (
              <>
                {' '}
                <button
                  type="button"
                  className={classes.viewMoreLink}
                  data-testid={`appointment-view-more-button-${appointment.id}`}
                  onClick={() => onViewMore(appointment)}
                >
                  {t('myDrugTesting.actions.viewMore')}
                </button>
              </>
            )}
          </p>
        </Box>

        <Box className={classes.resultStatusRow}>
          <Box className={classes.detailRow}>
            <p className={classes.detailLabel}>{t('myDrugTesting.card.status')}</p>
            <p className={statusClass}>{statusText}</p>
          </Box>
          <Box className={classes.detailRow}>
            <p className={classes.detailLabel}>{t('myDrugTesting.card.testResult')}</p>
            <p className={classes.detailValue}>{resultText}</p>
          </Box>
        </Box>

        {showBat && (
          <Box className={classes.detailRow}>
            <p className={classes.detailLabel}>{t('myDrugTesting.card.batResult')}</p>
            <p className={classes.detailValue}>{batResult}</p>
          </Box>
        )}

        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.details')}</p>
          <p className={classes.detailValue}>{detailsText}</p>
        </Box>
      </Box>

      {showMenu && (
        <ActionsMenu
          isOpen={menuOpen}
          onClose={handleMenuClose}
          position={menuPosition}
          testId="appointment-actions-menu"
          items={[
            ...(canEdit
              ? [
                  {
                    label: t('myDrugTesting.contextMenu.edit'),
                    testId: 'appointment-edit-button',
                    onClick: () => onEdit(appointment),
                  },
                ]
              : []),
            {
              label: t('myDrugTesting.contextMenu.deleteAppointment'),
              testId: 'appointment-delete-button',
              onClick: () => onDelete(appointment),
            },
          ]}
        />
      )}
    </Box>
  );
};

export default memo(AppointmentCard);
